import {
	IonButtons,
	IonHeader,
	IonIcon,
	IonPage,
	IonText,
	IonTitle,
	IonToolbar,
	useIonRouter,
	useIonViewDidEnter,
} from "@ionic/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { CoreValueAvatarWithBackground } from "@/components/CoreValue/AvatarWithBackground";
import { PersonalizeStatusCard } from "@/components/CoreValue/PersonalizeStatusCard";
import { FriendCheckProgress } from "@/components/FriendCheck/Progress";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { Toolbar } from "@/components/Global/Toolbar";
import { ReflectionProgress } from "@/components/Reflection/Progress";
import { arrowRightIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { useFeatureFlagsContext } from "@/lib/posthog/feature-flags/context";
import { queryOptions_coreValuesChosen } from "@/lib/query/functions/core-values/chosen";
import { queryOptions_coreValuesSummary } from "@/lib/query/functions/core-values/summary";
import { useMyData } from "@/lib/query/functions/user/data";
import { useMyProgression } from "@/lib/query/functions/user/progression";
import { Routes } from "@/lib/router";
import { useSelectorModalStore } from "@/lib/store/modal";

export function CoreValuesDashboard() {
	const { t } = useTranslation();
	const [myProgression] = useMyProgression();
	const presentModal = useSelectorModalStore.use.present();
	const [myData] = useMyData();
	const router = useIonRouter();
	const featureFlags = useFeatureFlagsContext();

	const queryCoreValuesChosen = useQuery({
		...queryOptions_coreValuesChosen(),
		enabled: myProgression.core_values === "SUMMARIZED",
	});

	const querySummaryCoreValues = useQuery({
		...queryOptions_coreValuesSummary({ type: "corevalues" }),
		enabled: myProgression.core_values === "SUMMARIZED",
	});

	const querySummaryFriendCheck = useQuery({
		...queryOptions_coreValuesSummary({ type: "friendcheck" }),
		enabled: myProgression.core_values === "SUMMARIZED",
	});

	useIonViewDidEnter(() => {
		queryCoreValuesChosen.refetch();
	});

	useEffect(() => {
		if (
			queryCoreValuesChosen.data &&
			queryCoreValuesChosen.data.length > 0 &&
			queryCoreValuesChosen.data?.filter((coreValue) => coreValue.customName).length ===
				queryCoreValuesChosen.data?.length &&
			!myData.coreValues.hasRegeneratedSummary
		) {
			presentModal("coreValuesResummarize");
		}
	}, [queryCoreValuesChosen.data, myData.coreValues.hasRegeneratedSummary]);

	return (
		<IonPage>
			<IonHeader>
				<Toolbar className="ion-p-2 plt-desktop:sm:ion-px-[max(calc(calc(100vw-640px)/2),--spacing(4))]">
					<IonTitle className="ion-text-brown-700 text-lg font-bold">
						{t("core-values.name")}
					</IonTitle>

					<IonButtons collapse slot="primary">
						<div className="size-10" />
					</IonButtons>
				</Toolbar>
			</IonHeader>
			<Content className="part-scroll:gap-8">
				<IonHeader collapse="condense" className="-mt-(--padding-top)">
					<IonToolbar className="ion-px-0">
						<IonTitle className="ion-text-brown-700 p-0 text-start text-3xl font-bold">
							{t("core-values.name")}
						</IonTitle>

						<IonButtons collapse slot="primary">
							<div className="size-10" />
						</IonButtons>
					</IonToolbar>
				</IonHeader>

				<CoreValueAvatarWithBackground
					className="py-8"
					coreValues={queryCoreValuesChosen.data?.map((coreValue) => coreValue.coreValue)}
				/>

				{myProgression.core_values !== "SUMMARIZED" && (
					<div className="space divide-brown-200 border-brown-200 flex flex-col divide-y rounded-2xl border drop-shadow contain-paint *:rounded-none *:border-x-0">
						<ReflectionProgress />
						<FriendCheckProgress />
					</div>
				)}

				<div className="flex flex-col gap-4">
					<PersonalizeStatusCard
						coreValues={queryCoreValuesChosen.data ?? []}
						onClick={(coreValue) => {
							if (featureFlags.canRefineCoreValue) {
								router.push(Routes.CoreValuesDetails({ id: coreValue.id }));
							} else {
								presentModal("coreValuesDetail", {
									item: {
										...coreValue.coreValue,
										name: coreValue.customName ?? coreValue.coreValue.name,
										reason: coreValue.description ?? coreValue.reason,
									},
								});
							}
						}}
					/>

					<motion.div
						className="border-brown-200 bg-brown-100 flex flex-col gap-2 rounded-xl border p-4"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 2 }}
					>
						<IonText className="text-brown-500 text-base font-semibold">{t("about-you")}</IonText>
						<IonText data-mask-text className="text-brown-700 text-lg font-semibold text-pretty">
							{querySummaryCoreValues.data}
						</IonText>
						<Button
							size="text"
							variant="clear"
							routerLink={Routes.ReflectionDetails()}
							routerDirection="forward"
							className="flex items-center"
						>
							<IonText>{t("page.reflection.details.read")}</IonText>
							<IonIcon slot="end" icon={arrowRightIcon} className="text-brown-400 size-4" />
						</Button>
					</motion.div>

					<motion.div
						className="border-brown-200 bg-brown-100 flex flex-col gap-2 rounded-xl border p-4"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 2.5 }}
					>
						<IonText className="text-brown-500 text-base font-semibold">
							{t("what-friends-say-about-you")}
						</IonText>

						<IonText data-mask-text className="text-brown-700 text-lg font-semibold text-pretty">
							{querySummaryFriendCheck.data}
						</IonText>

						<Button
							size="text"
							variant="clear"
							routerLink={Routes.FriendCheckDashboard()}
							routerDirection="forward"
							className="flex items-center"
						>
							<IonText>{t("page.friend-check.details.read")}</IonText>
							<IonIcon icon={arrowRightIcon} className="text-brown-400 size-4" />
						</Button>
					</motion.div>
				</div>
			</Content>
		</IonPage>
	);
}
